/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState } from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { useSelector } from "react-redux";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { getInputKeyByCheckBoxKey } from "../helpers";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { isValidDigit } from "../../../utils/validations";
import { JAVA_MAX_VALUE } from "../../../globals/globals";
import { RadixSelectControl } from "../../ui/radix-select";
import { Checkbox } from "../../ui/checkbox";
import { InputField } from "../../Shared/Radix/Inputs/InputField";

export const Reboot = () => {
    const dispatch = useStoreDispatch();
    const [disableChecks, setDisablechecks] = useState({
        radioNodeIdDisabled: false,
        radioUnitIdDisabled: false,
        sectorDeviceIdDisabled: false,
        cleanOptionDisabled: false,
    })

    const scheduledtasks = useSelector(tasksSelector);
    const errorMsg = useSelector(errorMsgSelector);

    const checkboxHandler = (event: any) => {
        const { name, checked } = event.target;
        // let rebootCheck: { [key: string]: boolean };
        if (name === "isRadioNodeId" && checked) {
            setDisablechecks({ radioNodeIdDisabled: false, radioUnitIdDisabled: true, sectorDeviceIdDisabled: true, cleanOptionDisabled: true });
        } else if ((name === "isRadioUnitId") && checked) {
            setDisablechecks({ radioNodeIdDisabled: true, radioUnitIdDisabled: false, sectorDeviceIdDisabled: false, cleanOptionDisabled: true });
        } else if ((name === "isSectorDeviceId" && scheduledtasks?.isRadioUnitId) && checked) {
            setDisablechecks({ radioNodeIdDisabled: true, radioUnitIdDisabled: false, sectorDeviceIdDisabled: false, cleanOptionDisabled: true });
        } else if ((name === "isSectorDeviceId" && scheduledtasks?.isRadioUnitId) && !checked) {
            setDisablechecks({ radioNodeIdDisabled: true, radioUnitIdDisabled: false, sectorDeviceIdDisabled: false, cleanOptionDisabled: true });
        } else if ((name === "isCleanOption" && checked) || (!scheduledtasks?.isRadioUnitId && !scheduledtasks?.isSectorDeviceId)) {
            setDisablechecks({ radioNodeIdDisabled: false, radioUnitIdDisabled: false, sectorDeviceIdDisabled: false, cleanOptionDisabled: false });
        } else {
            setDisablechecks({ radioNodeIdDisabled: false, radioUnitIdDisabled: false, sectorDeviceIdDisabled: false, cleanOptionDisabled: false });
        }
        if (!checked) {
            if (name === "isRadioUnitId") {
                dispatch(setScheduledTask({ [name]: checked, [getInputKeyByCheckBoxKey(name)]: "", isSectorDeviceId: false, sectorDeviceId: "" }));
            } else {
                dispatch(setScheduledTask({ [name]: checked, [getInputKeyByCheckBoxKey(name)]: "" }));
            }
        } else {
            dispatch(setScheduledTask({ [name]: checked }));
            
        }
    }

    const onInputChange = (name: string, value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (!!value && (!isValidDigit(value) || Number(value) < 0 || Number(value) > JAVA_MAX_VALUE)) {
            dispatch(setErrorMsg({ ...errorMsg, [name]: `Invalid ${name}` }));
        }
    };

    const selectOnChangeHandler = (value: string) => {
        dispatch(setScheduledTask({ cleanOption: value }));
        dispatch(setErrorMsg({ ...errorMsg, cleanOption: "" }));
    };

    return (
        <div className="flex flex-col gap-5">
            <div>
                <p className="text-sm font-medium">Reboot action parameters</p>
            </div>
            <div className="flex items-end justify-start">
                <div className="w-1/2">
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${disableChecks.radioNodeIdDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                        <Checkbox checked={scheduledtasks?.isRadioNodeId} name="isRadioNodeId" onCheckedChange={(checked) => checkboxHandler({ target: { name: "isRadioNodeId", checked } })} disabled={disableChecks.radioNodeIdDisabled} />
                        Selected radio node id
                    </label>
                </div>
                <div className="w-1/2">
                    <InputField
                        label=""
                        name="radioNodeId"
                        value={scheduledtasks?.radioNodeId ?? ""}
                        disabled={!scheduledtasks?.isRadioNodeId}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />
                </div>
            </div>
            <div className="flex items-end justify-start">
                <div className="w-1/2">
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${disableChecks.radioUnitIdDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                        <Checkbox checked={scheduledtasks?.isRadioUnitId} name="isRadioUnitId" onCheckedChange={(checked) => checkboxHandler({ target: { name: "isRadioUnitId", checked } })} disabled={disableChecks.radioUnitIdDisabled} />
                        Selected radio unit id
                    </label>
                </div>
                <div className="w-1/2">
                    <InputField
                        label=""
                        name="radioUnitId"
                        value={scheduledtasks?.radioUnitId ?? ""}
                        disabled={!scheduledtasks?.isRadioUnitId}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />
                </div>
            </div>
            <div className="flex items-end justify-start">
                <div className="w-1/2">
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${disableChecks.sectorDeviceIdDisabled || !scheduledtasks?.isRadioUnitId ? "opacity-50 pointer-events-none" : ""}`}>
                        <Checkbox checked={scheduledtasks?.isSectorDeviceId} name="isSectorDeviceId" onCheckedChange={(checked) => checkboxHandler({ target: { name: "isSectorDeviceId", checked } })} disabled={disableChecks.sectorDeviceIdDisabled || !scheduledtasks?.isRadioUnitId} />
                        Selected sector device id
                    </label>
                </div>
                <div className="w-1/2">
                    <InputField
                        label=""
                        name="sectorDeviceId"
                        value={scheduledtasks?.sectorDeviceId ?? ""}
                        disabled={!scheduledtasks?.isSectorDeviceId}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                    />
                </div>
            </div>
            <div className="flex items-end justify-start">
                <div className="w-1/2">
                    <label className={`flex items-center gap-2 text-sm cursor-pointer ${disableChecks.cleanOptionDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                        <Checkbox checked={scheduledtasks?.isCleanOption} name="isCleanOption" onCheckedChange={(checked) => checkboxHandler({ target: { name: "isCleanOption", checked } })} disabled={disableChecks.cleanOptionDisabled} />
                        Device reboot clean option
                    </label>
                </div>
                <div className="w-1/2">
                    <RadixSelectControl
                        options={[
                            { value: "None", label: "None" },
                            { value: "Db", label: "Db" },
                            { value: "Data", label: "Data" },
                            { value: "Configuration", label: "Configuration" },
                        ]}
                        value={scheduledtasks.cleanOption || "None"}
                        placeholder="__placeholder__"
                        onValueChange={selectOnChangeHandler}
                        disabled={!scheduledtasks?.isCleanOption || disableChecks.cleanOptionDisabled}
                    />
                </div>
            </div>
        </div>
    );
};